import React, { useState, useEffect, useRef } from "react";
import { Grid2, Tooltip } from "@mui/material";
import { DropdownStyles } from ".";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const Dropdown = ({
  options = [],
  placeholder = "Select an option",
  onSelect,
  value = [],
  className = "",
  style = {},
  title = "",
  subtitle = "",
  tooltip,
  dropdownHeader = "",
  dropdownList = "",
  multiple = false,
  error = "",
  errorMessage = "",
  disabled,
  fontSize,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const searchKeyTimeout = useRef(null);
  const [searchKey, setSearchKey] = useState("");

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prevState) => !prevState);
    }
  };

  const handleOptionClick = (option) => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : [];

      const isSelected = selectedValues?.some(
        (selected) => selected === option?.value
      );
      const newSelectedOptions = isSelected
        ? value.filter((selected) => selected !== option.value)
        : [...value, option?.value];

      onSelect(newSelectedOptions);
    } else {
      onSelect(option.value);
      setIsOpen(false);
    }
  };

  const handleKeyPress = (event) => {
    if (!isOpen) return;

    const key = event.key.toLowerCase();
    if (key.length === 1 && key.match(/[a-z0-9]/i)) {
      setSearchKey((prev) => prev + key);
      if (searchKeyTimeout.current) {
        clearTimeout(searchKeyTimeout.current);
      }
      searchKeyTimeout.current = setTimeout(() => {
        setSearchKey("");
      }, 1000);
      const firstMatchIndex = options.findIndex((option) =>
        option.label.toLowerCase().startsWith(searchKey + key)
      );
      if (firstMatchIndex >= 0 && listRef.current) {
        const matchingOption = listRef.current.children[firstMatchIndex];
        if (matchingOption) {
          matchingOption.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyPress);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyPress);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isOpen, searchKey]);

  return (
    <Grid2
      className={`${
        !disabled ? DropdownStyles.dropdown : DropdownStyles.disabled
      } ${className} ${error ? DropdownStyles.error : ""}`}
      style={style}
      ref={dropdownRef}
    >
      {title && (
        <div className={DropdownStyles.dropdownTitle}>
          <div className={`${fontSize ? DropdownStyles.fontSize : ""}`}>
            {title}
          </div>
          <div>
            {tooltip && (
              <Tooltip title={tooltip} arrow>
                <InfoOutlinedIcon className={DropdownStyles.infoIcon} />
              </Tooltip>
            )}
          </div>
        </div>
      )}
      {subtitle && (
        <div className={DropdownStyles.dropdownSubtitle}>{subtitle}</div>
      )}
      <div
        className={`${DropdownStyles.dropdownHeader} ${
          error ? DropdownStyles.error : ""
        } ${dropdownHeader}`}
        onClick={toggleDropdown}
      >
        <span className={DropdownStyles.dropdownLabel}>
          {value?.length === 0
            ? placeholder
            : multiple
            ? options
                .filter((option) => value?.includes(option.value))
                .map((opt) => opt.label)
                .join(", ")
            : (() => {
                const selectedOption = options.find(
                  (option) => option.value === value
                );
                return (
                  <div className={DropdownStyles.dropdownOption}>
                    {selectedOption?.img}
                    {selectedOption?.label}
                  </div>
                );
              })()}
        </span>

        <span
          className={`${DropdownStyles.dropdownArrow} ${
            isOpen ? DropdownStyles.up : DropdownStyles.down
          }`}
        />
      </div>
      {isOpen && (
        <div
          className={`${DropdownStyles.dropdownList} ${dropdownList}`}
          ref={listRef}
        >
          {options.map((option) => {
            const isSelected = multiple
              ? value?.includes(option.value)
              : value === option.value;
            return (
              <div
                key={option.value}
                className={`${DropdownStyles.dropdownOption} ${
                  isSelected ? DropdownStyles.selectedOption : ""
                }`}
                onClick={() => handleOptionClick(option)}
              >
                {option.img}
                {option.label}
              </div>
            );
          })}
        </div>
      )}
      {error && <div className={DropdownStyles.errorText}>{errorMessage}</div>}
    </Grid2>
  );
};

export default Dropdown;
